/**
 * Plain text out of CMS HTML without handing the markup to the live document (rank 51).
 *
 * `document.createElement( 'div' ).innerHTML = body` adopts the markup into the page's document even though the
 * element is never attached: every `<img>` in an article body starts downloading and inline `on*` handlers run on
 * load or error. A DOMParser document has no browsing context, so nothing in it fetches or runs. Where DOMParser
 * does not exist (the sync worker), `stripHtml` does the same job with regular expressions.
 */

export interface HtmlTextSummary {
    /** The text content, without `<script>`, `<style>` or `<noscript>`. Whitespace is left as the markup had it. */
    text: string;
    /** How many `<img>` elements the markup holds. */
    imageCount: number;
}

// Content that is never read as text
const NON_TEXT_SELECTOR: string = 'script, style, noscript';
const NON_TEXT_BLOCK: RegExp = /<(script|style|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const COMMENT: RegExp = /<!--[\s\S]*?-->/g;
// Block-level tags end a word; inline tags (<b>, <a>, <span>) do not
const BLOCK_TAG: RegExp = /<\/?(?:address|article|aside|blockquote|br|dd|div|dl|dt|figcaption|figure|footer|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b[^>]*>/gi;
// A '<' followed by a space is text in HTML, so only a tag name or '!' opens a tag
const ANY_TAG: RegExp = /<\/?[a-z!][^>]*>/gi;
const IMG_TAG: RegExp = /<img\b/gi;
const ENTITY: RegExp = /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi;
const NAMED_ENTITIES: Readonly< Record< string, string > > = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
    ndash: '–', mdash: '—', hellip: '…', bull: '•',
    copy: '©', reg: '®', trade: '™', deg: '°'
};

function decodeEntity( match: string, name: string ): string {
    if ( name[ 0 ] !== '#' ) return NAMED_ENTITIES[ name ] ?? match;

    const codePoint: number = name[ 1 ] === 'x' || name[ 1 ] === 'X' ? parseInt( name.slice( 2 ), 16 ) : parseInt( name.slice( 1 ), 10 );
    return codePoint > 0 && codePoint <= 0x10ffff ? String.fromCodePoint( codePoint ) : match;
}

/** Decodes numeric character references and the common named entities; anything else is left as written. */
export function decodeHtmlEntities( text: string ): string {
    return text.includes( '&' ) ? text.replace( ENTITY, decodeEntity ) : text;
}

/**
 * The text of an HTML fragment by string surgery alone: no DOM, so it is safe in a worker and cheap enough to run
 * over a whole archive. Block tags become spaces so paragraphs do not run together.
 */
export function stripHtml( html: string | null | undefined ): HtmlTextSummary {
    if ( !html ) return { text: '', imageCount: 0 };

    const withoutNonText: string = html.replace( COMMENT, '' ).replace( NON_TEXT_BLOCK, '' );
    const imageCount: number = withoutNonText.match( IMG_TAG )?.length ?? 0;
    const text: string = decodeHtmlEntities( withoutNonText.replace( BLOCK_TAG, ' ' ).replace( ANY_TAG, '' ) );
    return { text, imageCount };
}

/**
 * The text and image count of an HTML fragment, parsed inertly with DOMParser (falls back to `stripHtml` where
 * DOMParser does not exist).
 */
export function summarizeHtml( html: string | null | undefined ): HtmlTextSummary {
    if ( !html ) return { text: '', imageCount: 0 };
    if ( typeof DOMParser === 'undefined' ) return stripHtml( html );

    // textContent runs '<p>one</p><p>two</p>' together as 'onetwo'; a space after each block tag keeps words apart
    const parsed: Document = new DOMParser().parseFromString( html.replace( BLOCK_TAG, '$& ' ), 'text/html' );
    parsed.querySelectorAll( NON_TEXT_SELECTOR ).forEach( ( element: Element ) => element.remove() );

    return {
        text: parsed.body?.textContent ?? '',
        imageCount: parsed.body?.querySelectorAll( 'img' ).length ?? 0
    };
}

/** Runs of whitespace (including no-break spaces) folded to one space, trimmed. */
export function collapseWhitespace( text: string ): string {
    return text.replace( /\s+/g, ' ' ).trim();
}

/** The readable text of an HTML fragment on one line, parsed inertly. */
export function htmlToPlainText( html: string | null | undefined ): string {
    return collapseWhitespace( summarizeHtml( html ).text );
}

/** 'N Minute Read': 1,500 visible characters a minute plus 12 seconds an image, never under a minute. */
export function readingTimeLabel( { text, imageCount }: HtmlTextSummary ): string {
    const charNum: number = text.replace( /\s+/g, '' ).length;

    const timeForRound: number = 0.2;
    const time: number = Math.round( charNum / 1500 + imageCount * 0.2 + timeForRound );
    const parseTime: number = time < 1 ? 1 : time;
    return `${ parseTime } Minute Read`;
}
